import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Settings2 } from 'lucide-react';

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const SECTION_LABEL = { team: 'Team / Versand', max: 'Max', luis: 'Luis', thomas: 'Thomas', talu: 'Talu' };
const inp = 'h-7 text-xs bg-secondary border border-border rounded-sm px-1.5 text-foreground';

const DeadlinePicker = ({ value, onChange, testId }) => (
  <span className="inline-flex items-center gap-1">
    <select value={value?.day ?? 2} onChange={(e) => onChange({ ...value, day: Number(e.target.value) })} className={inp} data-testid={`${testId}-day`}>
      {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
    </select>
    <input type="time" value={value?.time || '18:00'} onChange={(e) => onChange({ ...value, time: e.target.value })} className={`${inp} w-[88px]`} data-testid={`${testId}-time`} />
  </span>
);

// Admin configuration: working hours (pace), rework end, per KPI deadline / window length / target / class targets.
export const KpiSettingsDialog = ({ open, onOpenChange, onSaved, weekOffset }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setCfg(null);
    api.getKpiConfig().then((r) => setCfg(r.data)).catch(() => { toast.error('KPI-Konfiguration konnte nicht geladen werden'); onOpenChange(false); });
  }, [open, onOpenChange]);

  const setKpi = (key, patch) => setCfg((c) => ({ ...c, kpis: { ...c.kpis, [key]: { ...c.kpis[key], ...patch } } }));
  const save = async () => {
    setSaving(true);
    try {
      const kpis = {};
      Object.entries(cfg.kpis).forEach(([k, v]) => {
        kpis[k] = { window_days: v.window_days, target: v.target, class_targets: v.class_targets || {} };
        if (v.deadline) kpis[k].deadline = v.deadline;
      });
      const r = await api.saveKpiConfig({ work_hours: cfg.work_hours, rework: cfg.rework, kpis, week_offset: weekOffset });
      onSaved(r.data);
      toast.success('KPI-Konfiguration gespeichert');
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };
  const keys = cfg ? Object.keys(cfg.kpis) : [];
  const toggleDay = (d) => setCfg((c) => {
    const days = new Set(c.work_hours.days);
    days.has(d) ? days.delete(d) : days.add(d);
    return { ...c, work_hours: { ...c.work_hours, days: [...days].sort() } };
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="kpi-settings-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-foreground flex items-center gap-2"><Settings2 className="w-4 h-4 text-sky-400" /> KPI-Einstellungen</DialogTitle></DialogHeader>
        {!cfg ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="border border-border rounded-sm p-3" data-testid="kpi-settings-work-hours">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Arbeitszeit (Soll-Linie steigt nur hier)</p>
                <div className="flex gap-1 mb-2">
                  {DAYS.map((d, i) => (
                    <button key={d} onClick={() => toggleDay(i)} data-testid={`kpi-settings-workday-${i}`}
                      className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm ${cfg.work_hours.days.includes(i) ? 'border-sky-500/50 bg-sky-500/10 text-sky-300' : 'border-border text-muted-foreground'}`}>{d}</button>
                  ))}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <input type="time" value={cfg.work_hours.start} onChange={(e) => setCfg({ ...cfg, work_hours: { ...cfg.work_hours, start: e.target.value } })} className={`${inp} w-[88px]`} data-testid="kpi-settings-work-start" />
                  <span>–</span>
                  <input type="time" value={cfg.work_hours.end} onChange={(e) => setCfg({ ...cfg, work_hours: { ...cfg.work_hours, end: e.target.value } })} className={`${inp} w-[88px]`} data-testid="kpi-settings-work-end" />
                </div>
              </div>
              <div className="border border-border rounded-sm p-3" data-testid="kpi-settings-rework">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Nacharbeit (nur Brief-KPIs)</p>
                <p className="text-[10px] text-muted-foreground mb-1.5">Wird ein Brief-Ziel verfehlt, zählen Briefe nach der Deadline bis zu diesem Zeitpunkt noch in die alte Woche — bis das Ziel steht.</p>
                <DeadlinePicker value={cfg.rework} onChange={(v) => setCfg({ ...cfg, rework: v })} testId="kpi-settings-rework" />
              </div>
            </div>
            <table className="w-full text-xs" data-testid="kpi-settings-table">
              <thead>
                <tr className="text-[9px] font-mono uppercase text-muted-foreground border-b border-border/60">
                  <th className="text-left py-1 font-normal">KPI</th><th className="text-left py-1 font-normal">Deadline</th>
                  <th className="text-left py-1 font-normal">Fenster (Tage)</th><th className="text-left py-1 font-normal">Ziel</th>
                  <th className="text-left py-1 font-normal">Ziele je Klasse (optional)</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const v = cfg.kpis[k]; const d = cfg.defs[k];
                  const first = keys.find((x) => cfg.defs[x].section === d.section) === k;
                  return (
                    <React.Fragment key={k}>
                      {first && <tr><td colSpan={5} className="pt-3 pb-1 text-[10px] font-mono uppercase tracking-wider text-sky-300">{SECTION_LABEL[d.section]}</td></tr>}
                      <tr className="border-b border-border/30" data-testid={`kpi-settings-row-${k}`}>
                        <td className="py-1.5 pr-2 text-foreground">{d.label}{d.weekly && <span className="block text-[9px] text-muted-foreground">Wochenziel direkt an der Karte änderbar</span>}</td>
                        <td className="py-1.5 pr-2">{d.has_deadline ? <DeadlinePicker value={v.deadline} onChange={(dl) => setKpi(k, { deadline: dl })} testId={`kpi-settings-${k}-deadline`} /> : <span className="text-muted-foreground">laufend</span>}</td>
                        <td className="py-1.5 pr-2">{d.has_deadline ? <input type="number" min="1" max="365" value={v.window_days ?? 7} onChange={(e) => setKpi(k, { window_days: Number(e.target.value) })} className={`${inp} w-16`} data-testid={`kpi-settings-${k}-window`} /> : '—'}</td>
                        <td className="py-1.5 pr-2">
                          {['max_calls', 'team_buffer'].includes(k)
                            ? <span className="text-muted-foreground">{k === 'team_buffer' ? 'Ø Versand/Woche' : 'automatisch (fällig)'}</span>
                            : <input type="number" min="0" step={d.kind === 'hours' ? '0.5' : '1'} value={v.target ?? ''} onChange={(e) => setKpi(k, { target: e.target.value === '' ? null : Number(e.target.value) })} className={`${inp} w-20`} data-testid={`kpi-settings-${k}-target`} />}
                          {d.kind === 'pct' && <span className="text-muted-foreground ml-1">%</span>}{d.kind === 'hours' && <span className="text-muted-foreground ml-1">h</span>}
                        </td>
                        <td className="py-1.5">
                          {d.has_split && d.kind === 'count' ? ['A', 'B', 'C'].map((c) => (
                            <span key={c} className="inline-flex items-center gap-0.5 mr-1.5 text-[10px] text-muted-foreground">{c}
                              <input type="number" min="0" value={v.class_targets?.[c] ?? ''} placeholder="–" onChange={(e) => setKpi(k, { class_targets: { ...(v.class_targets || {}), [c]: e.target.value === '' ? null : Number(e.target.value) } })} className={`${inp} w-12`} data-testid={`kpi-settings-${k}-class-${c}`} />
                            </span>
                          )) : <span className="text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="rounded-sm text-xs">Abbrechen</Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs" data-testid="kpi-settings-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Speichern'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
